import { createStore } from "zustand";
import type {
  DailyDeviceInfos,
  DailyEvent,
  DailyEventObject,
  DailyInputSettings,
} from "@daily-co/daily-js";

interface AgentStore {
  state:
    | "idle" // initial state
    | "preparing" // preparing to be ready
    | "ready" // ready (can call assistant.start/stop)
    | "call-starting"
    | "call-started"
    | "call-ending"
    | "error"
    | "call-ended";
  volumeLevel: number;
  instance:
    | {
        start: () => void;
        stop: () => void;
        initialize: (callbacks: AgentCallbacks) => (() => void) | null;
        changeAudioInput: (
          deviceId: string,
        ) => Promise<DailyDeviceInfos> | undefined;
        setCustomInputSettings: (
          settings: DailyInputSettings,
        ) => Promise<{ inputSettings: DailyInputSettings }> | undefined;
        setLocalAudio: (enabled: boolean) => void;
        getInputDevices: () => Promise<DailyDeviceInfos> | null;
        getInputSettings: () => Promise<DailyInputSettings> | null;
        sendSystemMessage: (content: string) => void;
        onDailyEvent: <T extends DailyEvent>(
          event: T,
          handler: (event: DailyEventObject<T>) => void,
        ) => (() => void) | void;
      }
    | undefined;
  audioStream: MediaStream | undefined;
  speaker: "assistant" | "user";
  stateHistory: AgentStore["state"][];
  setState: (state: AgentStore["state"]) => void;
  setData: (state: Partial<NonNullable<AgentStore["data"]>>) => void;
  setVolumeLevel: (volumeLevel: number) => void;
  setSpeaker: (speaker: "assistant" | "user") => void;
  setAudioStream: (track: MediaStream) => void;
  data?: { callId?: string; assistantId?: string };
}

export const agentStore = createStore<AgentStore>((set) => ({
  state: "idle",
  audioStream: undefined,
  volumeLevel: 0,
  speaker: "assistant",
  stateHistory: ["idle"],
  instance: undefined,
  data: undefined,
  setData: (data) =>
    set((prevState) => ({ data: { ...prevState.data, ...data } })),
  setState: (state) =>
    set((prevState) => ({
      state,
      stateHistory: [...prevState.stateHistory, state],
    })),
  setAudioStream: (track: MediaStream) => set({ audioStream: track }),
  setVolumeLevel: (volumeLevel) => set({ volumeLevel }),
  setSpeaker: (speaker) => set({ speaker }),
}));

export type AgentCallbacks = {
  onError?: (error: any) => void;
  onPlayable?: (e: { stream: MediaStream }) => void;
  onCallStart?: (args: { callId: string; assistantId: string }) => void;
  onCallEnd?: () => void;
  onSpeechStart?: () => void;
  onSpeechEnd?: () => void;
  onVolumeLevel?: (volume: number) => void;
  onNetworkConnection?: (
    connection: DailyEventObject<"network-connection">,
  ) => void;
};
